import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import useFirstRender from './use-first-render'
import usePrevious from './usePrevious'

export default function useSyncForm(
  useFormOptions: Parameters<typeof useForm>[0],
  keysWhichShouldSync: Array<Array<string>>,
) {
  const useFormRetVal = useForm({ ...useFormOptions, mode: 'onChange' })

  const formValues = useFormRetVal.watch()
  const isFirstRender = useFirstRender()

  const prevFormValues = usePrevious(formValues)

  useEffect(() => {
    if (isFirstRender) {
      return
    }

    keysWhichShouldSync.forEach(pairOfKeysToSync => {
      let shouldUpdateAllKeys: boolean = false
      let valueToUpdate = ''

      pairOfKeysToSync.forEach(key => {
        const currentVal = formValues[key]
        const prevVal = prevFormValues[key]
        const valIsNew = currentVal !== prevVal

        if (valIsNew) {
          shouldUpdateAllKeys = true
          valueToUpdate = currentVal
        }
      })

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (shouldUpdateAllKeys && valueToUpdate) {
        pairOfKeysToSync.forEach(key => {
          useFormRetVal.setValue(key, valueToUpdate)
        })
      }
    })
  }, [
    formValues,
    prevFormValues,
    keysWhichShouldSync,
    isFirstRender,
    useFormRetVal,
  ])

  useEffect(() => {
    if (isFirstRender) {
      if (!useFormOptions?.defaultValues) {
        throw new Error('Need to pass default values')
      }

      const defaultValues = useFormOptions.defaultValues

      keysWhichShouldSync.flat().forEach(key => {
        if (!Object.keys(defaultValues).includes(key)) {
          throw new Error(
            `key ${key} isn't given a default value in defaultValues option`,
          )
        }
      })

      /** for each pair check if default values are same, if not throw error */
      keysWhichShouldSync.forEach(pairOfKeysToSync => {
        const defaultValues: string[] = []

        pairOfKeysToSync.forEach(key => {
          //  to keep typescript happy
          if (useFormOptions.defaultValues?.[key]) {
            defaultValues.push(useFormOptions.defaultValues[key])
          }
        })

        if (!defaultValues.every(val => val === defaultValues[0])) {
          throw new Error(
            `${pairOfKeysToSync.join(
              ', ',
            )} form names' default values are not same. If they have to be in sync their default values should be same`,
          )
        }
      })
    }
  }, [
    isFirstRender,
    useFormRetVal,
    keysWhichShouldSync,
    useFormOptions?.defaultValues,
  ])

  return useFormRetVal
}
